
import { useEffect, useRef } from "react";
import { Toaster as SonnerToaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import RequestAccessPage from "./pages/RequestAccessPage";
import DataWorkspace from "./pages/DataWorkspace";
import EmailWorkspace from "./pages/EmailWorkspace";
import AnalyticsHub from "./pages/AnalyticsHub";
import NotFound from "./pages/NotFound";
import WorkspaceLayout from "./layouts/WorkspaceLayout";
import AIContext from "./pages/AIContext";
import SettingsPage from "./pages/SettingsPage";
import { GoogleAuthWrapper } from "./contexts/GoogleAuthContext";
import { LoadingProvider } from "./contexts/LoadingContext";
import { LoadingOverlays } from "./components/ui/LoadingOverlays";
import { RouteChangeHandler } from "./components/routing/RouteChangeHandler";
import LoginOverlay from "./components/auth/LoginOverlay";
import { supabase } from "./integrations/supabase/client";
import { toast } from "sonner";
import UpdateNotification from "./components/UpdateNotification";
import { clearInvestorLocalStorage } from "@/utils/investors/databaseUtils";

// Configure Query Client with better error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
      refetchOnWindowFocus: false,
      meta: {
        onError: (error: Error) => {
          console.error("Query error:", error);
          toast.error("An error occurred with data fetching");
        }
      }
    },
    mutations: {
      meta: {
        onError: (error: Error) => {
          console.error("Mutation error:", error);
          toast.error("An error occurred while updating data");
        }
      }
    }
  }
});

const AppContent = () => {
  const dataLoadTriggered = useRef(false);
  
  // Clear any existing investor data from localStorage on app initialization
  useEffect(() => {
    console.log("App initialization - clearing any investor data from localStorage");
    clearInvestorLocalStorage();
  }, []); 
  
  // Set up Supabase auth state listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN') {
          toast.success(`Successfully authenticated in Supabase${session?.user?.email ? ' as ' + session.user.email : ''}`);
          console.log("Supabase user ID:", session?.user?.id);
          
          // Mark that we should trigger data load
          dataLoadTriggered.current = true;
        } else if (event === 'SIGNED_OUT') {
          toast.info("Signed out from Supabase");
          // Clear React Query cache on logout
          queryClient.clear();
          // Reset the data load flag
          dataLoadTriggered.current = false;
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <LoadingProvider>
      <TooltipProvider>
        <SonnerToaster />
        <UpdateNotification autoReloadDelay={5} />
        <LoadingOverlays />
        <BrowserRouter>
          <LoginOverlay />
          <RouteChangeHandler />
          <Routes>
            {/* Landing page at root */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Request access page */}
            <Route path="/request-access" element={<RequestAccessPage />} />
            
            {/* Main workspaces */}
            <Route path="/analytics" element={
              <WorkspaceLayout workspace="analytics">
                <AnalyticsHub />
              </WorkspaceLayout>
            } />
            
            <Route path="/data" element={
              <WorkspaceLayout workspace="data">
                <DataWorkspace />
              </WorkspaceLayout>
            } />
            
            <Route path="/email" element={
              <WorkspaceLayout workspace="email">
                <EmailWorkspace />
              </WorkspaceLayout>
            } />
            
            {/* Configuration pages */}
            <Route path="/ai-context" element={
              <WorkspaceLayout workspace="analytics">
                <AIContext />
              </WorkspaceLayout>
            } />
            
            <Route path="/settings" element={
              <WorkspaceLayout workspace="analytics">
                <SettingsPage />
              </WorkspaceLayout>
            } />
            
            {/* Legacy redirects for backward compatibility */}
            <Route path="/dashboard" element={<Navigate to="/analytics" replace />} />
            <Route path="/upload" element={<Navigate to="/data?tab=upload" replace />} />
            <Route path="/investors" element={<Navigate to="/data?tab=investors" replace />} />
            <Route path="/contacts" element={<Navigate to="/data?tab=contacts" replace />} />
            <Route path="/email-generator" element={<Navigate to="/email" replace />} />
            <Route path="/drafts" element={<Navigate to="/email?tab=drafts" replace />} />
            <Route path="/sent" element={<Navigate to="/email?tab=sent" replace />} />
            <Route path="/refresh" element={<Navigate to="/analytics" replace />} />
            <Route path="/emails" element={<Navigate to="/email" replace />} />
            <Route path="/pricing" element={<Navigate to="/request-access" replace />} />
            
            {/* Catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </LoadingProvider>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <GoogleAuthWrapper>
        <AppContent />
      </GoogleAuthWrapper>
    </QueryClientProvider>
  );
};

export default App;
